import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../services/adminApi';
import type { ModerationQueueItem } from '../types';

export function ModerationQueue() {
  const queryClient = useQueryClient();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  // Surface the real backend error instead of silently spinning forever.
  const errMsg = (e: any) =>
    e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Request failed';

  // Fetch moderation queue items
  const { data: items, isLoading, error } = useQuery<ModerationQueueItem[]>({
    queryKey: ['moderationQueue'],
    queryFn: () => adminApi.getModerationQueue(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (id: string) => adminApi.approveModerationItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moderationQueue'] });
      setProcessingId(null);
      setActionNotice('Approved — content published to its destination.');
    },
    onError: (e: any) => {
      setProcessingId(null);
      setActionError(`Approve failed: ${errMsg(e)}`);
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: (id: string) => adminApi.rejectModerationItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moderationQueue'] });
      setProcessingId(null);
      setActionNotice('Rejected — content will not be published.');
    },
    onError: (e: any) => {
      setProcessingId(null);
      setActionError(`Reject failed: ${errMsg(e)}`);
    },
  });

  const handleApprove = async (item: ModerationQueueItem) => {
    setActionError(null);
    setActionNotice(null);
    setProcessingId(item.id);
    approveMutation.mutate(item.id);
  };

  const handleReject = async (item: ModerationQueueItem) => {
    setActionError(null);
    setActionNotice(null);
    if (!window.confirm('Reject and block this content? This cannot be undone.')) {
      setProcessingId(null);
      return;
    }
    setProcessingId(item.id);
    rejectMutation.mutate(item.id);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load moderation queue: {(error as Error).message}
      </Alert>
    );
  }

  const pendingItems = items?.filter(item => item.status === 'pending') || [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        🔍 Content Moderation Queue
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Review user-submitted content that was flagged by our automated safety system
      </Typography>

      {actionError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {actionNotice && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setActionNotice(null)}>
          {actionNotice}
        </Alert>
      )}

      {pendingItems.length === 0 ? (
        <Paper sx={{ p: 4, mt: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            ✅ No items pending review
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            All content has been reviewed!
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3, mt: 2 }}>
          {pendingItems.map((item) => (
            <Card key={item.id}>
                {item.media_data_uri && (
                  <CardMedia
                    component="img"
                    height="300"
                    image={item.media_data_uri}
                    alt="Content under review"
                    sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
                  />
                )}
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {item.media_type === 'profile_picture' && '👤 Profile Picture'}
                    {item.media_type === 'recipe_image' && '🍽️ Recipe Image'}
                    {item.media_type === 'recipe_video' && '🎥 Recipe Video'}
                  </Typography>
                  
                  <Box sx={{ mt: 1, mb: 2 }}>
                    {item.flagged_categories?.map((category) => (
                      <Chip
                        key={category}
                        label={category}
                        size="small"
                        color="warning"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </Box>

                  <Typography variant="caption" color="text.secondary" display="block">
                    Submitted: {new Date(item.submitted_at).toLocaleString()}
                  </Typography>
                  
                  {item.context_type && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Context: {item.context_type}
                    </Typography>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    color="success"
                    onClick={() => handleApprove(item)}
                    disabled={processingId === item.id}
                  >
                    ✅ Approve
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleReject(item)}
                    disabled={processingId === item.id}
                  >
                    ❌ Reject
                  </Button>
                  {processingId === item.id && (
                    <CircularProgress size={20} sx={{ ml: 1 }} />
                  )}
                </CardActions>
              </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}
